import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/useAuthStore';
import {
  featureModuleForApiPath,
  hasFeatureModulesCache,
  isFeatureEnabled,
} from '@/utils/featureModules';
import { attachRequirementEditSessionHeader } from '@/utils/requirementEditSession';
import { attachCollabClientSessionHeader } from '@/utils/collabClientSession';

export interface ApiError {
  message: string;
  status?: number;
  data?: unknown;
  /** 退出登录过程中的 401，调用方不应弹错 */
  silentAuth?: boolean;
}

const SPRING_FULL_AUTH_REQUIRED =
  /^full authentication is required to access this resource$/i;

function resolveAxiosErrorMessage(data: { message?: string; error?: string } | undefined, fallback: string): string {
  const msg = typeof data?.message === 'string' ? data.message.trim() : '';
  const err = typeof data?.error === 'string' ? data.error.trim() : '';
  // 优先友好中文 message；避免把 Spring 英文 InsufficientAuthenticationException 直接抛给用户
  if (msg && !SPRING_FULL_AUTH_REQUIRED.test(msg)) return msg;
  if (err && !SPRING_FULL_AUTH_REQUIRED.test(err)) return err;
  if (msg) return msg;
  if (SPRING_FULL_AUTH_REQUIRED.test(err) || SPRING_FULL_AUTH_REQUIRED.test(msg)) {
    return '未认证或登录已失效';
  }
  return fallback;
}

function isAuthLoggingOut(): boolean {
  try {
    return !!useAuthStore().loggingOut;
  } catch {
    return false;
  }
}

/** 默认 60s：飞书组织拉取、会话持久化、大 payload 等在弱网/大租户下常超过 10s，避免未捕获超时导致页面异常退出 */
const http: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 60000,
});

export const aiHttp: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 60000,
});

/** 与 useAuthStore 中一致，用于拦截器回退读取，确保请求一定带上 Token */
const ACCESS_TOKEN_KEY = 'aipd_access_token';

/** 未勾选「自动登录」时 Token 仅存 sessionStorage */
export function getBrowserStoredAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return (
      localStorage.getItem(ACCESS_TOKEN_KEY) ||
      sessionStorage.getItem(ACCESS_TOKEN_KEY)
    );
  } catch {
    return null;
  }
}

function getAccessToken(): string | null {
  try {
    const authStore = useAuthStore();
    if (authStore.accessToken) return authStore.accessToken;
  } catch (_) {
    /* Pinia 未就绪时从存储读取 */
  }
  return getBrowserStoredAccessToken();
}

http.interceptors.request.use((config: import('axios').AxiosRequestConfig) => {
  const token = getAccessToken();
  config.headers = config.headers ?? {};
  const headers = config.headers as Record<string, string>;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  attachRequirementEditSessionHeader(String(config.url || ''), headers);
  attachCollabClientSessionHeader(headers);
  // 功能开关已拉取且关闭时前端短路；未拉取前放行，由后端过滤器兜底
  const url = String(config.url || '');
  if (hasFeatureModulesCache() && !url.includes('/app/features')) {
    const moduleKey = featureModuleForApiPath(url);
    if (moduleKey && !isFeatureEnabled(moduleKey)) {
      return Promise.reject(Object.assign(new Error(`功能未启用: ${moduleKey}`), {
        config,
        isFeatureDisabled: true,
        featureModule: moduleKey,
      }));
    }
  }
  return config;
});

http.interceptors.response.use(
  (response: import('axios').AxiosResponse) => {
    // 检查响应头中是否有新的Token（自动续期）
    const newToken = response.headers['x-new-access-token'];
    if (newToken) {
      const authStore = useAuthStore();
      authStore.updateAccessToken(newToken);
    } else {
      // 无新 JWT 时仍顺延「自动登录」前端空闲窗口（滑动 2h，与登录时刻无关）
      try {
        const authStore = useAuthStore();
        if (getAccessToken()) {
          authStore.touchRememberSessionExpiry();
        }
      } catch {
        /* Pinia 未就绪等 */
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const authStore = useAuthStore();
    const requestUrl = String(error.config?.url || '');
    const isRefreshEndpoint = requestUrl.includes('/auth/wecom/refresh') || requestUrl.includes('/auth/feishu/callback') || requestUrl.includes('/auth/wecom/callback');
    const status = error.response?.status;
    const loggingOut = isAuthLoggingOut() || authStore.loggingOut;

    // 退出登录过程中的 401：不再刷新 Token，也不把 Spring 英文错误抛给页面
    if (loggingOut && status === 401) {
      const apiError: ApiError = {
        message: '',
        status: 401,
        data: error.response?.data,
        silentAuth: true,
      };
      return Promise.reject(apiError);
    }

    // 401错误，尝试刷新Token；刷新接口本身不再递归触发刷新
    if (!isRefreshEndpoint && status === 401 && authStore.refreshToken && !loggingOut) {
      try {
        const refreshed = await authStore.refreshAccessToken();
        if (refreshed && error.config) {
          const token = getAccessToken();
          if (token) {
            error.config.headers = error.config.headers ?? {};
            (error.config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
          }
          return http.request(error.config);
        }
      } catch (refreshError) {
        // 刷新失败，清除认证信息
        authStore.clearAuth();
        // 跳转到登录页（如果在浏览器环境）
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
    }

    // 统一转换为 ApiError，优先使用后端返回的友好 message
    const data = error.response?.data as { message?: string; error?: string } | undefined;
    const message = resolveAxiosErrorMessage(data, error.message || '请求失败');
    const apiError: ApiError = {
      message,
      status,
      data: error.response?.data,
      silentAuth:
        status === 401 && (!authStore.isAuthenticated || loggingOut) ? true : undefined,
    };
    return Promise.reject(apiError);
  },
);

function unwrapUpstreamDetailMessage(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return raw;
  try {
    const parsed = JSON.parse(trimmed) as { detail?: unknown; message?: unknown };
    if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
      return parsed.detail.trim();
    }
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
    /* not JSON */
  }
  return trimmed;
}

/** 将 axios 拦截器抛出的 ApiError 或 Error 转为可读文案 */
export function formatApiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && (err as ApiError).silentAuth) return '';
  if (err instanceof Error && err.message) return unwrapUpstreamDetailMessage(err.message);
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as ApiError).message;
    if (typeof m === 'string' && m.trim()) return unwrapUpstreamDetailMessage(m);
  }
  if (typeof err === 'string' && err.trim()) return unwrapUpstreamDetailMessage(err);
  return '请求失败';
}

/** 退出登录 / 未登录时的 401，页面不应再弹错误提示 */
export function isIgnorableAuthError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as ApiError;
  if (e.silentAuth) return true;
  if (e.status === 401) {
    try {
      const authStore = useAuthStore();
      if (authStore.loggingOut || !authStore.isAuthenticated) return true;
    } catch {
      return true;
    }
  }
  return false;
}

export async function request<T>(promise: Promise<AxiosResponse<T>>): Promise<T> {
  try {
    const res = await promise;
    return res.data;
  } catch (err) {
    throw err as ApiError;
  }
}

export default http;

